import { useState, useEffect } from "react";
import { downloadBlob, apiError, api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileText, Package, Download, Map, FileDown, FileSpreadsheet, Globe, Layers, ShieldCheck, Loader2, CheckCircle2, AlertTriangle, RefreshCw, Share2, Copy, RotateCcw, Clock, ChevronDown, Mountain, RotateCw, Save, ArrowUp, ArrowDown, Trash2, BarChart3 } from "lucide-react";
import { cleanName } from "@/lib/format";
import { Slider } from "@/components/ui/slider";
import HMSRankingDialog from "@/components/HMSRankingDialog";
import HMSAssumptionsCard from "@/components/HMSAssumptionsCard";
import { projectIsHms } from "@/lib/hms";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import GeoPiSectionsManager from "@/components/GeoPiSectionsManager";

const MAP_KEYS = [
  { key: "location", label: "Location" },
  { key: "spectral", label: "Spectral" },
  { key: "target", label: "Targets" },
  { key: "structural", label: "Structural" },
  { key: "confidence", label: "Confidence" },
];

export default function Step4Downloads({ project }) {
  const { hasPerm } = useAuth();
  const isSuper = hasPerm("manage_companies");
  const [busy, setBusy] = useState("");
  const [qa, setQa] = useState(null);
  const [masterOverride, setMasterOverride] = useState(false);
  const [log, setLog] = useState(null);
  const [showLog, setShowLog] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [shareExpiry, setShareExpiry] = useState("");
  const [reminderDays, setReminderDays] = useState("7");
  const [shareExpiresAt, setShareExpiresAt] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [showTimeline, setShowTimeline] = useState(false);
  const [zipJob, setZipJob] = useState(null);
  const [showHmsRanking, setShowHmsRanking] = useState(false);
  const [confReview, setConfReview] = useState(null);
  const pid = project.id;
  const areas = (project.areas || []).filter((a) => a.status === "complete");
  const pname = (project.project_number || project.name || "SYMIN").replace(/[^\w-]/g, "_");
  const [coverLogo, setCoverLogo] = useState(project.branding?.cover_logo || "auto");
  const [coverTemplate, setCoverTemplate] = useState(project.branding?.cover_template || "default");
  const tv = project.branding?.terrain3d_view || {};
  const [t3dArea, setT3dArea] = useState((areas[0] && areas[0].area_id) || "");
  const [t3dElev, setT3dElev] = useState(typeof tv.elev === "number" ? tv.elev : 34);
  const [t3dAzim, setT3dAzim] = useState(typeof tv.azim === "number" ? tv.azim : -122);
  const [t3dKey, setT3dKey] = useState(1);
  const [savingView, setSavingView] = useState(false);
  const t3dSrc = t3dArea ? `${process.env.REACT_APP_BACKEND_URL}/api/projects/${pid}/areas/${t3dArea}/terrain3d?elev=${Math.round(t3dElev)}&azim=${Math.round(t3dAzim)}&_=${t3dKey}` : null;
  const nudgeView = (dElev, dAzim) => {
    setT3dElev((e) => Math.max(12, Math.min(80, e + dElev)));
    setT3dAzim((a) => (((a + dAzim + 180) % 360 + 360) % 360) - 180);
    setT3dKey((k) => k + 1);
  };
  const applyPreset = (e, a) => { setT3dElev(e); setT3dAzim(a); setT3dKey((k) => k + 1); };
  const saveView = async () => {
    setSavingView(true);
    try { await api.post(`/projects/${pid}/report-config`, { terrain3d_elev: Math.round(t3dElev), terrain3d_azim: Math.round(t3dAzim) }); toast.success("Saved - every report's 3D terrain figure will use this angle"); }
    catch (e) { toast.error(apiError(e)); } finally { setSavingView(false); }
  };
  const setCover = async (v) => {
    setCoverLogo(v);
    try { await api.post(`/projects/${pid}/report-config`, { cover_logo: v }); toast.success("Cover logo updated"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setTemplate = async (v) => {
    setCoverTemplate(v);
    try { await api.post(`/projects/${pid}/report-config`, { cover_template: v }); toast.success("Cover style updated"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const dl = async (key, path, filename) => {
    setBusy(key);
    try { await downloadBlob(path, filename); toast.success("Download started"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const refreshSnapshot = async () => {
    setBusy("refresh");
    try { await api.post(`/projects/${pid}/snapshot/refresh`); toast.success("Portfolio preview regenerated"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const createShare = async () => {
    setBusy("share");
    try {
      const body = { reminder_days: Number(reminderDays) };
      if (shareExpiry) body.expires_days = Number(shareExpiry);
      const { data } = await api.post(`/projects/${pid}/share`, body);
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.og_path || `/api/share/${data.token}/og`}`;
      setShareUrl(url);
      setShareExpiresAt(data.expires_at || null);
      try { await navigator.clipboard.writeText(url); toast.success("Share link created & copied - shows a preview when pasted"); }
      catch (_) { toast.success("Share link created"); }
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const reissueShare = async () => {
    setBusy("reissue");
    try {
      const { data } = await api.post(`/projects/${pid}/share/reissue`, shareExpiry ? { expires_days: Number(shareExpiry) } : {});
      setShareExpiresAt(data.expires_at || null);
      toast.success("Link re-issued - expiry extended");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const revokeShare = async () => {
    try { await api.delete(`/projects/${pid}/share`); setShareUrl(""); setShareExpiresAt(null); toast.success("Share link moved to Trash - restore within 24h from Projects → Trash"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const deleteReportFile = async () => {
    try {
      await api.delete(`/projects/${pid}/master-zip`);
      setZipJob(null);
      toast.success("Report file moved to Trash - restore within 24h from Projects → Trash");
    } catch (e) { toast.error(apiError(e)); }
  };

  const pollZip = () => {
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/projects/${pid}/master-zip/status`);
        setZipJob(data);
        if (data.status === "ready") {
          clearInterval(t); setBusy("");
          toast.success("Master ZIP ready - downloading");
          downloadBlob(`/projects/${pid}/master-zip/download`, `${pname}.zip`).catch(() => {});
        } else if (data.status === "failed") {
          clearInterval(t); setBusy(""); toast.error(`Export failed: ${data.error || "unknown error"}`);
        }
      } catch (_) { /* keep polling */ }
    }, 3000);
  };

  const startZip = async () => {
    if (zipJob?.status === "ready") {
      return downloadBlob(`/projects/${pid}/master-zip/download`, `${pname}.zip`).catch((e) => toast.error(apiError(e)));
    }
    setBusy("zip");
    try {
      await api.post(`/projects/${pid}/master-zip/start`);
      setZipJob({ status: "building" });
      toast.info("Preparing your package in the background - you'll be notified when it's ready");
      pollZip();
    } catch (e) { toast.error(apiError(e)); setBusy(""); }
  };

  useEffect(() => {
    api.get(`/projects/${pid}/share`).then(({ data }) => {
      if (data.active && data.og_path) setShareUrl(`${process.env.REACT_APP_BACKEND_URL}${data.og_path}`);
      if (data.expires_at) setShareExpiresAt(data.expires_at);
      if (data.reminder_days) setReminderDays(String(data.reminder_days));
      if (data.expires_days) setShareExpiry(String(data.expires_days));
    }).catch(() => {});
    api.get(`/projects/${pid}/share/analytics`).then(({ data }) => setAnalytics(data)).catch(() => {});
    api.get(`/projects/${pid}/master-zip/status`).then(({ data }) => { if (data && data.status !== "none") setZipJob(data); }).catch(() => {});
  }, [pid]);

  const loadLog = async () => {
    try { const { data } = await api.get(`/projects/${pid}/conformance-log`); setLog(data.entries || []); } catch (_) { /* optional */ }
  };

  const dlMaster = async (key, path, filename) => {
    if (qa && qa.ok === false && !masterOverride) { toast.error("Export blocked by the conformance validator"); return; }
    setBusy(key);
    try { await downloadBlob(path, filename); toast.success("Download started"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const runQA = async () => {
    setBusy("qa");
    try {
      const { data } = await api.get(`/projects/${pid}/master-report/qa`);
      setQa(data); setMasterOverride(false); loadLog();
      const failed = (data.conformance?.checks || []).filter((c) => !c.passed).length;
      data.ok ? toast.success(`Conformance passed - ${data.score}/100`)
              : toast.warning(`Conformance failed - ${data.score}/100 · ${failed} check(s) failed`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const overrideMaster = async () => {
    const failed = (qa?.conformance?.checks || []).filter((c) => !c.passed).map((c) => c.label);
    try {
      await api.post(`/projects/${pid}/master-report/conformance-override`, { failed_checks: failed, score: qa?.score, checks: qa?.conformance?.checks || [] });
      setMasterOverride(true);
      loadLog();
      toast.warning("Conformance override recorded (logged with your name & timestamp) - export enabled");
    } catch (e) { toast.error(apiError(e)); }
  };

  const _downloadAreaPdf = async (a) => {
    await downloadBlob(`/projects/${pid}/areas/${a.area_id}/report/pdf`, `${a.area_id}_Report.pdf`);
    toast.success("Download started");
  };

  const dlAreaReport = async (a) => {
    const key = `${a.area_id}-r`;
    setBusy(key);
    try {
      let conf = null;
      try { const r = await api.get(`/projects/${pid}/areas/${a.area_id}/report/qa`); conf = r.data; }
      catch (_) { toast.warning("Conformance check could not be run - exporting without validation"); }
      if (conf && conf.ok === false) {
        const failed = (conf.conformance?.checks || []).filter((c) => !c.passed).map((c) => c.label);
        if (!isSuper) { toast.error(`Report blocked - conformance ${conf.score}/100 · ${failed.length} check(s) failed. Ask a Super-Admin to override.`); setBusy(""); return; }
        setBusy(""); setConfReview({ a, failed, score: conf.score, checks: conf.conformance?.checks || [] });
        return;
      }
      await _downloadAreaPdf(a);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  const proceedAreaReport = async () => {
    const review = confReview;
    if (!review) return;
    const a = review.a;
    setConfReview(null);
    setBusy(`${a.area_id}-r`);
    try {
      await api.post(`/projects/${pid}/areas/${a.area_id}/report/conformance-override`, { failed_checks: review.failed, score: review.score, checks: review.checks });
      loadLog();
      toast.warning("Override recorded - exporting");
      await _downloadAreaPdf(a);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  useEffect(() => {
    if (!areas.length) return;
    (async () => {
      try { const { data } = await api.get(`/projects/${pid}/master-report/qa`); setQa(data); setMasterOverride(false); } catch (_) { /* optional */ }
      loadLog();
    })();
  }, [pid]);

  const masterBlocked = !!qa && qa.ok === false && !masterOverride;

  return (
    <div className="space-y-6" data-testid="step4-downloads">
      <AlertDialog open={!!confReview} onOpenChange={(o) => { if (!o) setConfReview(null); }}>
        <AlertDialogContent data-testid="conformance-override-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><ShieldCheck size={18} className="text-primary" /> Conformance check failed · {confReview?.score}/100</AlertDialogTitle>
            <AlertDialogDescription>
              {confReview?.a?.area_id} did not pass {confReview?.failed?.length} conformance check(s). Overriding exports the report anyway and is logged with your name and timestamp.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {confReview?.failed?.length > 0 && (
            <ul className="max-h-40 overflow-auto rounded-md border border-border bg-muted/40 p-3 space-y-1 text-sm text-foreground" data-testid="conformance-fail-list">
              {confReview.failed.map((f, i) => (
                <li key={i} className="flex items-start gap-2"><AlertTriangle size={14} className="text-red-500 mt-0.5 shrink-0" />{f}</li>
              ))}
            </ul>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="conformance-override-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="conformance-override-confirm" onClick={proceedAreaReport} className="bg-red-600 hover:bg-red-700 text-white">Override &amp; export</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div>
        <h2 className="text-xl font-bold text-foreground tracking-tight">Reports &amp; Download Centre</h2>
        <p className="text-sm text-muted-foreground">{areas.length} area(s) analysed. Download the master combined report, individual area reports, maps and data.</p>
      </div>

      {/* Master */}
      <div className="rounded-xl p-5 grid md:grid-cols-3 gap-3 border border-[#C20511]/25 shadow-lg" style={{ background: "#0B1420" }} data-testid="master-block">
        <button data-testid="dl-master-report" disabled={busy === "master" || masterBlocked}
          onClick={() => dlMaster("master", `/projects/${pid}/master-report/pdf`, `${pname}_Master_Report.pdf`)}
          className="flex items-center gap-3 bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 rounded-lg p-4 text-left transition-all disabled:opacity-50">
          <span className="h-10 w-10 rounded-md grid place-items-center shrink-0" style={{ background: "#C20511", color: "#FFFFFF" }}><FileText size={19} /></span>
          <span><span className="block text-sm font-bold text-white">{busy === "master" ? "Preparing…" : "Consolidated Master Report (PDF)"}</span>
            <span className="block text-[11px] text-white/55">All areas &amp; every mineral · one consolidated PDF</span></span>
        </button>
        <button data-testid="dl-master-report-docx" disabled={busy === "masterdoc" || masterBlocked}
          onClick={() => dlMaster("masterdoc", `/projects/${pid}/master-report/docx`, `${pname}_Master_Report.docx`)}
          className="flex items-center gap-3 bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 rounded-lg p-4 text-left transition-all disabled:opacity-50">
          <span className="h-10 w-10 rounded-md bg-blue-500 text-white grid place-items-center shrink-0"><FileText size={19} /></span>
          <span><span className="block text-sm font-bold text-white">{busy === "masterdoc" ? "Preparing…" : "Master Report (Word)"}</span>
            <span className="block text-[11px] text-white/55">Same format · editable .docx</span></span>
        </button>
        <button data-testid="dl-master-zip" disabled={busy === "zip" || masterBlocked}
          onClick={startZip}
          className="flex items-center gap-3 bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 rounded-lg p-4 text-left transition-all disabled:opacity-50">
          <span className="h-10 w-10 rounded-md bg-white/10 text-white grid place-items-center shrink-0">
            {busy === "zip" || zipJob?.status === "building" ? <Loader2 size={19} className="animate-spin" /> : zipJob?.status === "ready" ? <CheckCircle2 size={19} /> : <Package size={19} />}
          </span>
          <span><span className="block text-sm font-bold text-white">
            {busy === "zip" || zipJob?.status === "building" ? "Preparing…" : zipJob?.status === "ready" ? "Download All (ZIP)" : "Prepare All (ZIP)"}</span>
            <span className="block text-[11px] text-white/55">
              {zipJob?.status === "ready" ? "Ready · click to download" : busy === "zip" || zipJob?.status === "building" ? "Building in background…" : "Every area's 18-map suite + reports · builds in background"}</span></span>
        </button>
        {zipJob?.status === "ready" && (
          <button data-testid="delete-report-file" onClick={deleteReportFile} title="Move the stored report ZIP to Trash (restorable for 24h)"
            className="flex items-center gap-3 bg-white/[0.06] hover:bg-red-500/20 border border-white/10 rounded-lg p-4 text-left transition-all">
            <span className="h-10 w-10 rounded-md bg-red-500/80 text-white grid place-items-center shrink-0"><Trash2 size={19} /></span>
            <span><span className="block text-sm font-bold text-white">Delete Report File</span>
              <span className="block text-[11px] text-white/55">Moves to Trash · restore within 24h</span></span>
          </button>
        )}
        <button data-testid="dl-portfolio-snapshot" disabled={busy === "snap"}
          onClick={() => dl("snap", `/projects/${pid}/snapshot`, `${pname}_Summary.png`)}
          className="flex items-center gap-3 bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 rounded-lg p-4 text-left transition-all disabled:opacity-50">
          <span className="h-10 w-10 rounded-md text-white grid place-items-center shrink-0" style={{ backgroundColor: "#C20511" }}><Map size={19} /></span>
          <span><span className="block text-sm font-bold text-white">{busy === "snap" ? "Preparing…" : "Portfolio Summary"}</span>
            <span className="block text-[11px] text-white/55">Shareable cover preview (PNG)</span></span>
        </button>
        <button data-testid="dl-snapshot-refresh" disabled={busy === "refresh"} title="Regenerate the preview after editing branding or targets"
          onClick={refreshSnapshot}
          className="flex items-center gap-3 bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 rounded-lg p-4 text-left transition-all disabled:opacity-50">
          <span className="h-10 w-10 rounded-md bg-white/10 text-white grid place-items-center shrink-0"><RefreshCw size={19} className={busy === "refresh" ? "animate-spin" : ""} /></span>
          <span><span className="block text-sm font-bold text-white">{busy === "refresh" ? "Refreshing…" : "Refresh Preview"}</span>
            <span className="block text-[11px] text-white/55">Rebuild the summary image</span></span>
        </button>
        {projectIsHms(project) && (
          <button data-testid="open-hms-ranking" onClick={() => setShowHmsRanking(true)}
            className="flex items-center gap-3 bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 rounded-lg p-4 text-left transition-all">
            <span className="h-10 w-10 rounded-md text-white grid place-items-center shrink-0" style={{ background: "#C20511" }}><BarChart3 size={19} /></span>
            <span><span className="block text-sm font-bold text-white">Portfolio HMS Ranking</span>
              <span className="block text-[11px] text-white/55">Rank licences by coastal HMS corridor strength</span></span>
          </button>
        )}
      </div>

      {projectIsHms(project) && (
        <>
          <HMSRankingDialog open={showHmsRanking} onOpenChange={setShowHmsRanking} projectId={pid} />
          <HMSAssumptionsCard projectId={pid} />
        </>
      )}

      {/* GeoPi report sections (inserted from GeoPi · ordered into the Master Report) */}
      <GeoPiSectionsManager project={project} />

      {/* Client share link + social preview */}
      <div className="bg-card border border-border rounded-xl p-4" data-testid="share-block">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5">
            <span className="h-9 w-9 rounded-md text-white grid place-items-center shrink-0" style={{ backgroundColor: "#C20511" }}><Share2 size={18} /></span>
            <div>
              <div className="text-sm font-bold text-foreground">Client Share Link</div>
              <div className="text-[11px] text-muted-foreground">Public read-only link - shows the portfolio summary preview when pasted into email or chat</div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={reminderDays} onValueChange={setReminderDays}>
              <SelectTrigger data-testid="share-reminder-days" title="Remind me this many days before the link expires"
                className="h-10 w-auto min-w-[170px] rounded-md border-[#C20511]/50 text-[#0F172A] bg-card shadow-sm hover:border-[#C20511] focus:ring-1 focus:ring-[#C20511]/60 text-sm font-medium">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-[#C20511]/30">
                <SelectItem value="3" className="focus:bg-[#faf6ec] focus:text-[#0F172A]">Remind 3 days before</SelectItem>
                <SelectItem value="7" className="focus:bg-[#faf6ec] focus:text-[#0F172A]">Remind 7 days before</SelectItem>
                <SelectItem value="14" className="focus:bg-[#faf6ec] focus:text-[#0F172A]">Remind 14 days before</SelectItem>
              </SelectContent>
            </Select>
            <Select value={shareExpiry || "none"} onValueChange={(v) => setShareExpiry(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="share-expiry"
                className="h-10 w-auto min-w-[150px] rounded-md border-[#C20511]/50 text-[#0F172A] bg-card shadow-sm hover:border-[#C20511] focus:ring-1 focus:ring-[#C20511]/60 text-sm font-medium">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="border-[#C20511]/30">
                <SelectItem value="none" className="focus:bg-[#faf6ec] focus:text-[#0F172A]">No expiry</SelectItem>
                <SelectItem value="7" className="focus:bg-[#faf6ec] focus:text-[#0F172A]">Expires in 7 days</SelectItem>
                <SelectItem value="30" className="focus:bg-[#faf6ec] focus:text-[#0F172A]">Expires in 30 days</SelectItem>
                <SelectItem value="90" className="focus:bg-[#faf6ec] focus:text-[#0F172A]">Expires in 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="create-share-btn" onClick={createShare} disabled={busy === "share"} variant="outline" className="border-border h-10">
              <Share2 size={15} className="mr-2" />{busy === "share" ? "Creating…" : "Create Share Link"}
            </Button>
            {shareUrl && (
              <Button data-testid="reissue-share-btn" onClick={reissueShare} disabled={busy === "reissue"} variant="outline" className="border-border h-10" title="Extend the expiry and reset the reminder">
                <RotateCcw size={15} className={`mr-2 ${busy === "reissue" ? "animate-spin" : ""}`} />Re-issue
              </Button>
            )}
            {shareUrl && (
              <Button data-testid="revoke-share-btn" onClick={revokeShare} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 h-10">Revoke</Button>
            )}
          </div>
        </div>
        {shareUrl && shareExpiresAt && (
          <div className="mt-2 text-[11px] text-muted-foreground" data-testid="share-expiry-info">
            Expires {new Date(shareExpiresAt).toLocaleDateString()} · reminder {reminderDays} day(s) before
          </div>
        )}
        {shareUrl && (
          <div className="mt-3 flex items-center gap-2 bg-[#faf6ec] border border-[#e7d9b0] rounded-lg px-3 py-2" data-testid="share-url-bar">
            <Share2 size={14} className="shrink-0" style={{ color: "#C20511" }} />
            <input readOnly value={shareUrl} data-testid="share-url-input"
              className="flex-1 bg-transparent text-sm text-foreground outline-none" onFocus={(e) => e.target.select()} />
            <button onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Copied"); }}
              data-testid="share-url-copy" className="text-muted-foreground hover:text-[#C20511]"><Copy size={15} /></button>
          </div>
        )}
        {analytics && (analytics.opens > 0 || analytics.downloads > 0) && (
          <div className="mt-3 pt-3 border-t border-border" data-testid="share-analytics">
            <div className="flex flex-wrap items-center gap-5 text-xs text-muted-foreground">
              <span className="font-semibold uppercase tracking-wide text-muted-foreground">Client engagement</span>
              <span><b className="text-foreground text-sm">{analytics.opens}</b> open{analytics.opens === 1 ? "" : "s"}</span>
              <span><b className="text-foreground text-sm">{analytics.downloads}</b> download{analytics.downloads === 1 ? "" : "s"}</span>
              {analytics.last_activity && <span>Last activity {new Date(analytics.last_activity).toLocaleString()}</span>}
              <button data-testid="toggle-share-timeline" onClick={() => setShowTimeline((s) => !s)}
                className="ml-auto inline-flex items-center gap-1 text-[#C20511] hover:underline font-medium">
                <Clock size={13} /> {showTimeline ? "Hide timeline" : "View timeline"}
                <ChevronDown size={13} className={`transition-transform ${showTimeline ? "rotate-180" : ""}`} />
              </button>
            </div>
            {showTimeline && (
              <div className="mt-3 max-h-56 overflow-auto rounded-lg border border-border divide-y divide-border" data-testid="share-timeline">
                {(analytics.timeline || []).length === 0 ? (
                  <div className="px-3 py-3 text-xs text-muted-foreground">No activity recorded yet.</div>
                ) : (
                  (analytics.timeline || []).map((e, i) => (
                    <div key={i} className="px-3 py-2 flex items-center justify-between gap-3" data-testid="timeline-row">
                      <span className="flex items-center gap-2 text-xs text-foreground">
                        <span className={`h-1.5 w-1.5 rounded-full ${String(e.event).startsWith("download") ? "bg-emerald-500" : "bg-[#C20511]"}`} />
                        {e.label}
                      </span>
                      <span className="text-[11px] text-muted-foreground">{e.ts ? new Date(e.ts).toLocaleString() : ""}</span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Cover logo toggle */}
      <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-3 flex-wrap" data-testid="cover-logo-block">
        <span className="h-9 w-9 rounded-md bg-primary text-primary-foreground grid place-items-center shrink-0"><FileText size={18} /></span>
        <div className="mr-2">
          <div className="text-sm font-bold text-foreground">Report Cover Logo</div>
          <div className="text-[11px] text-muted-foreground">Choose which logo appears on the master report cover (Auto = client logo if uploaded, else SYMIN gold)</div>
        </div>
        <div className="inline-flex rounded-md border border-border overflow-hidden ml-auto" data-testid="cover-logo-toggle">
          {[["auto", "Auto"], ["symin", "SYMIN Gold"], ["client", "Client Logo"]].map(([v, l]) => (
            <button key={v} data-testid={`cover-logo-${v}`} onClick={() => setCover(v)}
              className={`px-3 h-9 text-sm transition-colors ${coverLogo === v ? "bg-primary text-primary-foreground" : "bg-card text-foreground hover:bg-muted"}`}>{l}</button>
          ))}
        </div>
      </div>

      {/* Cover template */}
      <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-3 flex-wrap" data-testid="cover-template-block">
        <span className="h-9 w-9 rounded-md text-white grid place-items-center shrink-0" style={{ backgroundColor: "#0F172A" }}><Layers size={18} /></span>
        <div className="mr-2">
          <div className="text-sm font-bold text-foreground">Report Cover Style</div>
          <div className="text-[11px] text-muted-foreground">Pick this project&apos;s cover look, or use the workspace default set in Admin → Branding</div>
        </div>
        <div className="inline-flex rounded-md border border-border overflow-hidden ml-auto" data-testid="cover-template-toggle">
          {[["default", "Default"], ["navy", "Navy"], ["light", "Light"], ["minimal", "Minimal"]].map(([v, l]) => (
            <button key={v} data-testid={`cover-template-${v}`} onClick={() => setTemplate(v)}
              className={`px-3 h-9 text-sm transition-colors ${coverTemplate === v ? "bg-primary text-primary-foreground" : "bg-card text-foreground hover:bg-muted"}`}>{l}</button>
          ))}
        </div>
      </div>

      {/* 3D Terrain Camera */}
      {areas.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-4" data-testid="terrain-camera-block">
          <div className="flex items-center gap-2.5 mb-3 flex-wrap">
            <span className="h-9 w-9 rounded-md text-white grid place-items-center shrink-0" style={{ backgroundColor: "#0F172A" }}><Mountain size={18} /></span>
            <div className="mr-2">
              <div className="text-sm font-bold text-foreground">3D Terrain Camera</div>
              <div className="text-[11px] text-muted-foreground">Spin the model to your preferred angle - this exact view is used for the 3D terrain figure in every report.</div>
            </div>
            {areas.length > 1 && (
              <Select value={t3dArea} onValueChange={(v) => { setT3dArea(v); setT3dKey((k) => k + 1); }}>
                <SelectTrigger data-testid="terrain-cam-area"
                  className="ml-auto h-9 w-auto min-w-[190px] rounded-md border-[#0F172A]/25 text-[#0F172A] bg-card shadow-sm hover:border-[#C20511] focus:ring-1 focus:ring-[#C20511]/60 text-sm font-medium">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-[#C20511]/30">
                  {areas.map((a) => (
                    <SelectItem key={a.area_id} value={a.area_id} className="focus:bg-[#faf6ec] focus:text-[#0F172A]">{`${a.area_id} · ${cleanName(a.name)}`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="grid md:grid-cols-2 gap-4 items-start">
            <div className="rounded-lg border border-border bg-muted overflow-hidden min-h-[220px] grid place-items-center">
              {t3dSrc ? <img key={t3dKey} src={t3dSrc} alt="3D terrain preview" data-testid="terrain-cam-preview" className="w-full object-contain" />
                : <div className="text-xs text-muted-foreground p-6">No terrain available</div>}
            </div>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>Tilt (elevation)</span><span className="tabular-nums font-mono">{Math.round(t3dElev)}°</span></div>
                <Slider min={12} max={80} step={2} value={[t3dElev]} data-testid="terrain-cam-elev"
                  onValueChange={(v) => setT3dElev(v[0])} onValueCommit={() => setT3dKey((k) => k + 1)} />
              </div>
              <div>
                <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>Rotation (azimuth)</span><span className="tabular-nums font-mono">{Math.round(t3dAzim)}°</span></div>
                <Slider min={-180} max={180} step={5} value={[t3dAzim]} data-testid="terrain-cam-azim"
                  onValueChange={(v) => setT3dAzim(v[0])} onValueCommit={() => setT3dKey((k) => k + 1)} />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button variant="outline" size="sm" className="h-8 border-border" onClick={() => nudgeView(0, -30)} data-testid="terrain-cam-left"><RotateCcw size={14} className="mr-1" />Left</Button>
                <Button variant="outline" size="sm" className="h-8 border-border" onClick={() => nudgeView(0, 30)} data-testid="terrain-cam-right"><RotateCw size={14} className="mr-1" />Right</Button>
                <Button variant="outline" size="sm" className="h-8 border-border" onClick={() => nudgeView(8, 0)} data-testid="terrain-cam-up"><ArrowUp size={14} className="mr-1" />Tilt up</Button>
                <Button variant="outline" size="sm" className="h-8 border-border" onClick={() => nudgeView(-8, 0)} data-testid="terrain-cam-down"><ArrowDown size={14} className="mr-1" />Tilt down</Button>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] text-muted-foreground mr-1">One-tap presets:</span>
                <Button variant="outline" size="sm" className="h-8 border-border" onClick={() => applyPreset(68, -90)} data-testid="terrain-preset-aerial">Aerial</Button>
                <Button variant="outline" size="sm" className="h-8 border-border" onClick={() => applyPreset(34, -122)} data-testid="terrain-preset-oblique">Oblique</Button>
                <Button variant="outline" size="sm" className="h-8 border-border" onClick={() => applyPreset(16, -90)} data-testid="terrain-preset-profile">Profile</Button>
              </div>
              <Button onClick={saveView} disabled={savingView} className="bg-primary hover:bg-primary/90 text-primary-foreground h-10 w-full" data-testid="terrain-cam-save">
                {savingView ? <><Loader2 size={15} className="mr-2 animate-spin" />Saving…</> : <><Save size={15} className="mr-2" />Save view for reports</>}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Pre-Export Conformance Validator (Section 32) */}
      <div className="bg-card border border-border rounded-xl p-4" data-testid="conformance-block">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5">
            <span className="h-9 w-9 rounded-md bg-primary text-primary-foreground grid place-items-center shrink-0"><ShieldCheck size={18} /></span>
            <div>
              <div className="text-sm font-bold text-foreground">Pre-Export Conformance Validator</div>
              <div className="text-[11px] text-muted-foreground">Section 32 - coordinates, UTM/EPSG, duplicate targets, 0–100 scores, P1/P2/P3, map furniture, figure/table numbering, technical reporting disclaimer &amp; Resource/Reserve wording</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {qa && (
              <span data-testid="conformance-score" className={`px-2.5 h-9 grid place-items-center rounded-md text-sm font-bold ${qa.score === 100 ? "bg-emerald-100 text-emerald-700" : qa.score >= 70 ? "bg-red-100 text-red-700" : "bg-red-100 text-red-700"}`}>{qa.score}/100</span>
            )}
            <Button data-testid="run-conformance" onClick={runQA} disabled={busy === "qa"} variant="outline" className="border-border h-9">
              {busy === "qa" ? <><Loader2 size={15} className="mr-2 animate-spin" />Checking…</> : "Run Conformance Check"}
            </Button>
          </div>
        </div>
        {qa && qa.conformance && (
          <div className="mt-3 pt-3 border-t border-border space-y-1.5" data-testid="conformance-result">
            {qa.conformance.checks.map((c) => (
              <div key={c.id} data-testid={`conformance-check-${c.id}`} className="flex items-start gap-2 text-[13px]">
                {c.passed ? <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-emerald-600" /> : <AlertTriangle size={15} className="mt-0.5 shrink-0 text-red-600" />}
                <span className={c.passed ? "text-foreground" : "text-red-700 font-medium"}><span className="font-semibold">{c.label}.</span> <span className="text-muted-foreground">{c.detail}</span></span>
              </div>
            ))}
            {!qa.ok && (
              <div className="mt-3 flex items-center justify-between gap-3 flex-wrap rounded-md bg-red-50 border border-red-200 px-3 py-2" data-testid="conformance-blocked">
                <span className="text-[13px] font-semibold text-red-700">Export blocked - resolve the failed checks above before exporting.</span>
                {isSuper && !masterOverride && (
                  <Button data-testid="conformance-override-btn" onClick={overrideMaster} className="h-9 bg-red-600 hover:bg-red-700 text-white">Override &amp; Export (logged)</Button>
                )}
                {masterOverride && <span data-testid="conformance-overridden" className="text-[13px] font-semibold text-red-700">Override recorded - export enabled.</span>}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Conformance History Log (read-only audit trail) */}
      <div className="bg-card border border-border rounded-xl p-4" data-testid="conformance-log-block">
        <button onClick={() => { setShowLog((s) => !s); if (!log) loadLog(); }} data-testid="toggle-conformance-log"
          className="w-full flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <span className="h-9 w-9 rounded-md bg-slate-900 text-white grid place-items-center shrink-0"><Clock size={16} /></span>
            <div className="text-left">
              <div className="text-sm font-bold text-foreground">Conformance History</div>
              <div className="text-[11px] text-muted-foreground">Audit trail of every Section 32 validation &amp; Super-Admin override · read-only{log ? ` · ${log.length} entr${log.length === 1 ? "y" : "ies"}` : ""}</div>
            </div>
          </div>
          <ChevronDown size={18} className={`text-muted-foreground transition-transform ${showLog ? "rotate-180" : ""}`} />
        </button>
        {showLog && (
          <div className="mt-3 pt-3 border-t border-border space-y-2" data-testid="conformance-log-list">
            {(!log || log.length === 0) && <div className="text-sm text-muted-foreground" data-testid="conformance-log-empty">No conformance checks recorded yet. Run the validator above to create the first entry.</div>}
            {log && log.map((e, i) => <LogEntry key={i} entry={e} />)}
          </div>
        )}
      </div>

      {/* Per-area */}
      <div className="space-y-3" data-testid="area-downloads">
        {areas.map((a) => (
          <div key={a.area_id} className="bg-card border border-border rounded-xl shadow-sm p-4" data-testid={`dl-area-${a.area_id}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="h-9 w-9 rounded-lg bg-primary text-primary-foreground grid place-items-center font-extrabold text-xs">{a.area_id}</div>
              <div className="min-w-0"><div className="text-sm font-semibold text-foreground truncate">{cleanName(a.name)}</div>
                <div className="text-[11px] text-muted-foreground">{a.target_count || 0} targets · {a.high_priority_count || 0} high priority</div></div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Pill testid={`dl-${a.area_id}-report`} icon={FileText} label="Report (PDF)" busy={busy === `${a.area_id}-r`}
                onClick={() => dlAreaReport(a)} />
              {MAP_KEYS.map((mk) => (
                <Pill key={mk.key} testid={`dl-${a.area_id}-map-${mk.key}`} icon={Map} label={mk.label} busy={busy === `${a.area_id}-${mk.key}`}
                  onClick={() => dl(`${a.area_id}-${mk.key}`, `/projects/${pid}/areas/${a.area_id}/map/${mk.key}`, `${a.area_id}_${mk.key}.png`)} />
              ))}
              <Pill testid={`dl-${a.area_id}-kml`} icon={FileDown} label="Targets KML" busy={busy === `${a.area_id}-k`}
                onClick={() => dl(`${a.area_id}-k`, `/projects/${pid}/areas/${a.area_id}/export/kml`, `${a.area_id}_targets.kml`)} />
              <Pill testid={`dl-${a.area_id}-csv`} icon={FileSpreadsheet} label="Targets CSV" busy={busy === `${a.area_id}-c`}
                onClick={() => dl(`${a.area_id}-c`, `/projects/${pid}/areas/${a.area_id}/export/csv`, `${a.area_id}_targets.csv`)} />
              <Pill testid={`dl-${a.area_id}-xlsx`} icon={FileSpreadsheet} label="Targets XLSX" busy={busy === `${a.area_id}-x`}
                onClick={() => dl(`${a.area_id}-x`, `/projects/${pid}/areas/${a.area_id}/export/xlsx`, `${a.area_id}_targets.xlsx`)} />
              <Pill testid={`dl-${a.area_id}-geojson`} icon={Globe} label="GeoJSON" busy={busy === `${a.area_id}-g`}
                onClick={() => dl(`${a.area_id}-g`, `/projects/${pid}/areas/${a.area_id}/export/geojson`, `${a.area_id}_targets.geojson`)} />
              <Pill testid={`dl-${a.area_id}-kmz`} icon={Globe} label="KMZ" busy={busy === `${a.area_id}-z`}
                onClick={() => dl(`${a.area_id}-z`, `/projects/${pid}/areas/${a.area_id}/export/kmz`, `${a.area_id}_targets.kmz`)} />
              <Pill testid={`dl-${a.area_id}-shp`} icon={Layers} label="Shapefile" busy={busy === `${a.area_id}-s`}
                onClick={() => dl(`${a.area_id}-s`, `/projects/${pid}/areas/${a.area_id}/export/shp`, `${a.area_id}_shapefile.zip`)} />
              <Pill testid={`dl-${a.area_id}-gpkg`} icon={Layers} label="GeoPackage" busy={busy === `${a.area_id}-p`}
                onClick={() => dl(`${a.area_id}-p`, `/projects/${pid}/areas/${a.area_id}/export/gpkg`, `${a.area_id}_targets.gpkg`)} />
            </div>
          </div>
        ))}
      </div>

      {areas.length === 0 && (
        <div className="border-2 border-dashed border-border rounded-xl py-16 text-center text-muted-foreground" data-testid="downloads-empty">
          <Layers size={40} className="mx-auto mb-3 text-muted-foreground" />
          Process at least one area to enable downloads.
        </div>
      )}
    </div>
  );
}

const LogEntry = ({ entry }) => {
  const [open, setOpen] = useState(false);
  let dt = entry.at;
  try { dt = new Date(entry.at).toLocaleString(); } catch (_) { /* keep raw */ }
  const scope = entry.scope === "area" ? `Area ${entry.area_id}` : "Master report";
  const status = entry.override ? "OVERRIDE" : entry.ok ? "PASS" : "FAIL";
  const color = entry.override ? "text-red-700 bg-red-100" : entry.ok ? "text-emerald-700 bg-emerald-100" : "text-red-700 bg-red-100";
  const failed = (entry.checks || []).filter((c) => !c.passed).length;
  return (
    <div className="border border-border rounded-lg p-2.5" data-testid="conformance-log-entry">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 text-left">
        <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${color}`}>{status}</span>
        <span className="text-sm font-semibold text-foreground">{entry.score}/100</span>
        <span className="text-[12px] text-muted-foreground">· {scope}</span>
        <span className="ml-auto text-[11px] text-muted-foreground truncate max-w-[220px]">{dt} · {entry.by_name}</span>
        <ChevronDown size={14} className={`text-muted-foreground transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-2 pt-2 border-t border-border grid sm:grid-cols-2 gap-x-4 gap-y-1">
          {(entry.checks || []).map((c) => (
            <div key={c.id} className="flex items-start gap-1.5 text-[12px]">
              {c.passed ? <CheckCircle2 size={13} className="mt-0.5 shrink-0 text-emerald-600" /> : <AlertTriangle size={13} className="mt-0.5 shrink-0 text-red-600" />}
              <span className={c.passed ? "text-muted-foreground" : "text-red-700 font-medium"}>{c.label}</span>
            </div>
          ))}
          {entry.override && <div className="sm:col-span-2 text-[12px] text-red-700 font-medium mt-1">Super-Admin override - exported despite {failed} failed check(s).</div>}
        </div>
      )}
    </div>
  );
};

const Pill = ({ testid, icon: Icon, label, onClick, busy }) => (
  <button data-testid={testid} onClick={onClick} disabled={busy}
    className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-border text-xs font-semibold text-foreground hover:border-primary/60 hover:text-primary transition-all disabled:opacity-50">
    <Icon size={14} /> {label} <Download size={12} className="text-muted-foreground" />
  </button>
);
